import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, User, Globe, Palette, ArrowRight, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import { trackEvent } from '../lib/analytics';

// Default color and member color options (new brand palette)
const DEFAULT_COLOR = '#15a2a2'; // Teal (primary)
const memberColors = [
  '#15a2a2', // Teal (primary)
  '#fe7434', // Orange (strong accent)
  '#1184a7', // Teal Dark (secondary)
  '#fea802', // Amber (highlights)
  '#ea515f', // Coral (warm accent)
  '#6fb1a0', // Sage (success)
  '#046dc8', // Blue (links)
  '#b4418e', // Magenta (member color)
  '#d94a8c', // Pink (member color)
  '#0450b4', // Navy (deep accent)
];

// Timezone options (from Settings)
const timezones = [
  // Australia & Pacific
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  // Asia
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (IST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  // Americas
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  // Other
  { value: 'UTC', label: 'UTC' },
];

interface OnboardingDefaults {
  familyName: string;
  timezone: string;
  userName: string;
  userColor?: string;
}

interface OnboardingStatus {
  needsOnboarding: boolean;
  defaults: OnboardingDefaults;
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [familyName, setFamilyName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [userName, setUserName] = useState('');
  const [userColor, setUserColor] = useState(DEFAULT_COLOR);

  // Track initial values to detect changes
  const [initialValues, setInitialValues] = useState<OnboardingDefaults | null>(null);

  // Detect browser timezone
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    async function loadOnboardingStatus() {
      try {
        const status = await api.get<OnboardingStatus>('/onboarding/status');

        // If user has already completed onboarding, redirect to home
        if (!status.needsOnboarding) {
          navigate('/', { replace: true });
          return;
        }

        // Set form defaults
        setFamilyName(status.defaults.familyName);
        setUserName(status.defaults.userName);
        setUserColor(status.defaults.userColor || DEFAULT_COLOR);

        // Use detected timezone if family timezone is UTC (default)
        // Otherwise use the family's timezone
        const effectiveTimezone =
          status.defaults.timezone === 'UTC' && detectedTimezone
            ? detectedTimezone
            : status.defaults.timezone;
        setTimezone(effectiveTimezone);

        setInitialValues(status.defaults);

        // Track onboarding started
        trackEvent('onboarding_started', { source: 'first_login' });
      } catch (err) {
        setError('Failed to load setup data. Please refresh the page.');
        console.error('Failed to load onboarding status:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadOnboardingStatus();
  }, [navigate, detectedTimezone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Only send changed values
      const updates: Record<string, string> = {};
      if (initialValues) {
        if (familyName !== initialValues.familyName) updates.familyName = familyName;
        if (timezone !== initialValues.timezone) updates.timezone = timezone;
        if (userName !== initialValues.userName) updates.userName = userName;
        const initialColor = initialValues.userColor ?? DEFAULT_COLOR;
        if (userColor !== initialColor) updates.userColor = userColor;
      }

      await api.post('/onboarding/complete', updates);

      // Navigate to home
      navigate('/', { replace: true });
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Failed to complete onboarding:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    try {
      await api.post('/onboarding/skip');
      trackEvent('onboarding_skipped', {});
      navigate('/', { replace: true });
    } catch {
      // Even if the skip tracking fails, let them proceed
      navigate('/', { replace: true });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="flex items-center gap-2 text-text-secondary">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-soft-cyan rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-text-primary" />
          </div>
          <h1 className="text-2xl font-semibold text-text-primary">Welcome to Family Calendar</h1>
          <p className="text-text-secondary mt-2">Let's get your household set up</p>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="card space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Family Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
              <User className="w-4 h-4" />
              Family Name
            </label>
            <input
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              className="w-full px-3 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
              placeholder="Enter family name"
              required
            />
          </div>

          {/* Timezone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
              <Globe className="w-4 h-4" />
              Your Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-3 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan bg-white"
              required
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                  {tz.value === detectedTimezone ? ' (detected)' : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted mt-1">Used for event scheduling and reminders</p>
          </div>

          {/* Your Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
              <User className="w-4 h-4" />
              Your Name
            </label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              className="w-full px-3 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
              placeholder="Enter your name"
              required
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-2">
              <Palette className="w-4 h-4" />
              Your Color
            </label>
            <p className="text-xs text-text-muted mb-2">
              This color identifies you on the calendar
            </p>
            <div className="flex gap-2 flex-wrap">
              {memberColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setUserColor(color)}
                  className={`w-10 h-10 rounded-full border-2 transition-all ${
                    userColor === color
                      ? 'border-text-primary scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn btn-primary py-3 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Setting up...</span>
              </>
            ) : (
              <>
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        {/* Skip Link */}
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={handleSkip}
            className="text-text-secondary hover:text-text-primary text-sm transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
